/* @flow */
import React, { Fragment } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import ChairHeart from 'dibs-icons/exports/ChairHeart';
import House from 'dibs-icons/exports/House';
import TriangleRuler from 'dibs-icons/exports/TriangleRuler';
import CouchLamp from 'dibs-icons/exports/CouchLamp';
import Blueprint from 'dibs-icons/exports/Blueprint';

import { PictureSelection } from './fields/PictureSelection';
import { MatchmakerButton } from './fields/MatchmakerButton';
import { RadioBar } from './fields/RadioBar';
import { ErrorMessage } from './fields/ErrorMessage';
import { LastElement } from './fields/LastElement';

import stepPreferenceStyles from './styles/FormStepPreference.scss';
import formStyles from './styles/FormGeneral.scss';

import { SubText } from './fields/SubText';

import type { ProjectScope } from '../constants';

const preferenceScopes = (
    <FormattedMessage
        id="abt.matchmaker.formStepPreference.preferenceScope"
        defaultMessage="1. How can we help you design your space?"
    />
);

const preference1 = (
    <FormattedMessage
        id="abt.matchmaker.formStepPreference.preference1"
        defaultMessage="2. What's your style?"
    />
);

const preference2 = (
    <FormattedMessage
        id="abt.matchmaker.formStepPreference.preference2"
        defaultMessage="3. Which color palette do you prefer?"
    />
);

const preference3 = (
    <FormattedMessage
        id="abt.matchmaker.formStepPreference.preference3"
        defaultMessage="4. How do you want to design your space?"
    />
);

const timeLabels = defineMessages({
    traditional: {
        id: 'abt.matchmaker.formStepPreference.traditional',
        defaultMessage: 'traditional',
    },
    mixTime: {
        id: 'abt.matchmaker.formStepPreference.mixTime',
        defaultMessage: 'a mix of both',
    },
    modern: {
        id: 'abt.matchmaker.formStepPreference.modern',
        defaultMessage: 'modern',
    },
});

const colorLabels = defineMessages({
    neutral: {
        id: 'abt.matchmaker.formStepPreference.neutral',
        defaultMessage: 'neutral',
    },
    mixColor: {
        id: 'abt.matchmaker.formStepPreference.mixColor',
        defaultMessage: 'a mix of both',
    },
    colorful: {
        id: 'abt.matchmaker.formStepPreference.colorful',
        defaultMessage: 'colorful',
    },
});

const layerLabels = defineMessages({
    layered: {
        id: 'abt.matchmaker.formStepPreference.layered',
        defaultMessage: 'layered',
    },
    mixLayer: {
        id: 'abt.matchmaker.formStepPreference.mixLayer',
        defaultMessage: 'a mix of both',
    },
    clean: {
        id: 'abt.matchmaker.formStepPreference.clean',
        defaultMessage: 'clean',
    },
});

const TIME_TRADITIONAL: 'traditional' = 'traditional';
const TIME_MIXED: 'mixTime' = 'mixTime';
const TIME_MODERN: 'modern' = 'modern';

export type Time = typeof TIME_TRADITIONAL | typeof TIME_MIXED | typeof TIME_MODERN;

const COLOR_NEUTRAL: 'neutral' = 'neutral';
const COLOR_MIXED: 'mixColor' = 'mixColor';
const COLOR_COLORFUL: 'colorful' = 'colorful';

export type Color = typeof COLOR_NEUTRAL | typeof COLOR_MIXED | typeof COLOR_COLORFUL;

const LAYER_LAYERED: 'layered' = 'layered';
const LAYER_MIXLAYER: 'mixLayer' = 'mixLayer';
const LAYER_CLEAN: 'clean' = 'clean';

export type Layer = typeof LAYER_LAYERED | typeof LAYER_MIXLAYER | typeof LAYER_CLEAN;

const timeArray: Array<Time> = [TIME_TRADITIONAL, TIME_MIXED, TIME_MODERN];
const colorArray: Array<Color> = [COLOR_NEUTRAL, COLOR_MIXED, COLOR_COLORFUL];
const layerArray: Array<Layer> = [LAYER_LAYERED, LAYER_MIXLAYER, LAYER_CLEAN];

const iconPaths = [
    'https://a.1stdibscdn.com/assets/trade/matchmaker/style_traditional.jpg',
    'https://a.1stdibscdn.com/assets/trade/matchmaker/style_modern.jpg',
    'https://a.1stdibscdn.com/assets/trade/matchmaker/style_neutral.jpg',
    'https://a.1stdibscdn.com/assets/trade/matchmaker/style_colorful.jpg',
    'https://a.1stdibscdn.com/assets/trade/matchmaker/style_layered.jpg',
    'https://a.1stdibscdn.com/assets/trade/matchmaker/style_clean.jpg',
];

const iconNodes = iconPaths.map(iconPath => {
    const iconStyle = {
        backgroundImage: `url("${iconPath}")`,
    };

    return (
        <div className={stepPreferenceStyles.iconContainer}>
            <div className={stepPreferenceStyles.icon} style={iconStyle} />
        </div>
    );
});

const scopeList: Array<{| name: ProjectScope, label: Element |}> = [
    {
        name: 'fullArchitectureDesign',
        label: (
            <FormattedMessage
                id="abt.matchmaker.MatchmakerContainer.fullArchitectureDesign"
                defaultMessage="Full architecture and design"
            />
        ),
        icon: <TriangleRuler />,
    },
    {
        name: 'fullHouseDesign',
        label: (
            <FormattedMessage
                id="abt.matchmaker.MatchmakerContainer.fullHouseDesign"
                defaultMessage="Full house design"
            />
        ),
        icon: <House />,
    },
    {
        name: 'designCoupleRooms',
        label: (
            <FormattedMessage
                id="abt.matchmaker.MatchmakerContainer.designCoupleRooms"
                defaultMessage="Design of multiple rooms"
            />
        ),
        icon: <Blueprint />,
    },
    {
        name: 'designSingleRoom',
        label: (
            <FormattedMessage
                id="abt.matchmaker.MatchmakerContainer.designSingleRoom"
                defaultMessage="Design of single room"
            />
        ),
        icon: <CouchLamp />,
    },
    {
        name: 'helpSingleItem',
        label: (
            <FormattedMessage
                id="abt.matchmaker.MatchmakerContainer.helpSingleItem"
                defaultMessage="Help with a single item"
            />
        ),
        icon: <ChairHeart />,
    },
];

export const FormStepPreference = (props: {|
    color: ?Color,
    time: ?Time,
    layer: ?Layer,
    projectScopes: Array<ProjectScope>,
    onChangeProjectScope: (scope: ProjectScope) => () => void,
    onChangeTime: (time: Time) => void,
    onChangeColor: (color: Color) => void,
    onChangeLayer: (layer: Layer) => void,
    onPageChange: () => void,
    goToPhotoSelect: () => void,
    errors: {
        projectScopes?: string,
        time?: string,
        layer?: string,
        color?: string,
    },
|}) => (
    <Fragment>
        <div className={formStyles.divider} />
        <div className={formStyles.contentWrapper}>
            <div id="jsScrollTargetScopePreference" className={stepPreferenceStyles.item}>
                <SubText text={preferenceScopes} />
                <div className={stepPreferenceStyles.scopeWrapper}>
                    {scopeList.map(field => {
                        const scopeName = field.name;
                        const scopeButton = (
                            <div className={stepPreferenceStyles.scopeContainer}>
                                <div className={stepPreferenceStyles.scopeSVG}>{field.icon}</div>
                                <div className={stepPreferenceStyles.scopeButtonText}>
                                    {field.label}
                                </div>
                            </div>
                        );
                        return (
                            <PictureSelection
                                onChange={props.onChangeProjectScope(scopeName)}
                                imageInput={scopeButton}
                                name={scopeName}
                                key={`projectScopes-${scopeName}`}
                                checked={props.projectScopes.includes(scopeName)}
                                selectedStyle={stepPreferenceStyles.selected}
                                originalStyle={stepPreferenceStyles.original}
                            />
                        );
                    })}
                    <ErrorMessage errors={props.errors.projectScopes} />
                </div>
            </div>
        </div>
        <RadioBar
            scrollId="jsScrollTargetTimePreference"
            question={preference1}
            buttons={timeArray}
            label={timeLabels}
            onChange={props.onChangeTime}
            selected={props.time}
            pictureLeft={iconNodes[0]}
            pictureRight={iconNodes[1]}
            error={props.errors.time}
        />
        <RadioBar
            scrollId="jsScrollTargetColorPreference"
            question={preference2}
            buttons={colorArray}
            label={colorLabels}
            onChange={props.onChangeColor}
            selected={props.color}
            pictureLeft={iconNodes[2]}
            pictureRight={iconNodes[3]}
            error={props.errors.color}
        />
        <RadioBar
            scrollId="jsScrollTargetLayerPreference"
            question={preference3}
            buttons={layerArray}
            label={layerLabels}
            onChange={props.onChangeLayer}
            selected={props.layer}
            pictureLeft={iconNodes[4]}
            pictureRight={iconNodes[5]}
            error={props.errors.layer}
        />

        <div className={stepPreferenceStyles.subPageDirectRow}>
            <a onClick={props.goToPhotoSelect}>
                <FormattedMessage
                    id="abt.matchmaker.formStepPreference.noMatch"
                    defaultMessage="These don't quite match my preferences"
                />
            </a>
        </div>
        <div id="jsScrollTargetMoreInfo" className={formStyles.contentWrapper}>
            <LastElement
                child={
                    <MatchmakerButton
                        classNames={stepPreferenceStyles.ctaNext}
                        onClick={props.onPageChange}
                        buttonName={
                            <FormattedMessage
                                id="abt.matchmaker.formStepPreference.getStartedButton"
                                defaultMessage="Next"
                            />
                        }
                        fullWidth
                    />
                }
            />
        </div>
    </Fragment>
);
